// Operation model — the foundation for multi-device (server/client) sync.
//
// Historically every mutation in the app persisted a *whole collection* back to
// storage (e.g. db.saveProducts(entireList)). That is fine on one device, but
// under multi-terminal sync it loses updates: two counters editing different
// products would each save their own full list and clobber the other's change.
//
// This module reframes every change as a small, replayable **Operation** keyed
// by the entity it touches (e.g. `products` / upsert / <id>). An operation is:
//   • conflict-friendly — two devices upserting *different* products merge
//     cleanly; two upserting the *same* one resolve last-writer-wins by a
//     Lamport clock (logical time) with the device id as a deterministic
//     tie-break, so every device converges on the same result;
//   • replayable — applyOp() is the single write path into IndexedDB, used
//     identically for a local mutation and for one received from the server.
//
// Phase 0 (this file + PosContext wiring) keeps single-device behaviour
// identical: emitOp() applies locally exactly as the old db.saveX did, and —
// until a sync transport registers via registerOpSink() — nothing is queued or
// sent anywhere. Phase 1 plugs the LAN server in behind registerOpSink() and
// feeds inbound ops through applyRemoteOp(); no mutator has to change again.

import * as db from './db';
import { newId } from './db';
import { cachedRawKey, idbGetRaw, idbSetRaw } from './idb';
import { getDeviceId } from './license';

// upsert/delete address a single row in a list collection by `key` (its id);
// `set` replaces a whole singleton value (config, settings, active shift, …);
// `adjustStock` applies commutative per-product deltas so simultaneous sales on
// two terminals both count instead of overwriting each other.
export type OpAction = 'upsert' | 'delete' | 'set' | 'adjustStock';

export interface Operation {
  id: string; // globally-unique op id (dedupe key across devices)
  deviceId: string; // origin device
  lamport: number; // logical clock — orders ops for last-writer-wins
  ts: string; // wall-clock ISO, for display / human audit only
  seq?: number; // server-assigned global order; set once the server acks it
  collection: string; // registry key below, e.g. 'products'
  action: OpAction;
  key?: string; // entity id for upsert/delete
  value?: unknown; // entity (upsert) | whole value (set) | delta list (adjustStock)
}

// ---- Lamport logical clock ------------------------------------------------
// A monotonic counter shared by every op this device emits. On receiving a
// remote op we advance past its value (observeLamport), so causally-later local
// ops always sort after the remote ones. Persisted as a plain string via the
// same synchronously-readable cache the rest of the app uses, and primed at
// boot by primeCaches().
const lamportCache = cachedRawKey('epos.lamport', '0');

function currentLamport(): number {
  return parseInt(lamportCache.get() || '0', 10) || 0;
}

function nextLamport(): number {
  const n = currentLamport() + 1;
  lamportCache.set(String(n)); // in-memory update is synchronous; persist is best-effort
  return n;
}

function observeLamport(n: number): void {
  if (n > currentLamport()) lamportCache.set(String(n));
}

// ---- Last-writer-wins version map -----------------------------------------
// Tracks, per addressed row ("<collection>/<key>"), the Lamport time + device
// of the newest op we've applied, so a late-arriving *older* op for the same
// row is ignored. Kept in memory and mirrored to IDB so the guard survives a
// restart. Only consulted for remote ops — local ops are newest by construction
// (they just took the next Lamport tick).
const VERSIONS_KEY = 'epos.opVersions';
type Version = { lamport: number; deviceId: string };
let versions: Record<string, Version> = {};

async function loadVersions(): Promise<void> {
  const raw = await idbGetRaw(VERSIONS_KEY);
  if (raw) {
    try {
      versions = JSON.parse(raw) as Record<string, Version>;
    } catch {
      versions = {};
    }
  }
}

let persistTimer: ReturnType<typeof setTimeout> | null = null;
function persistVersions(): void {
  // Debounced: a checkout can touch many rows at once; one write covers them.
  if (persistTimer) return;
  persistTimer = setTimeout(() => {
    persistTimer = null;
    idbSetRaw(VERSIONS_KEY, JSON.stringify(versions)).catch(() => {});
  }, 250);
}

// True when `op` is newer than what we've already applied to its row (or the
// row is untracked). Ties (same Lamport time from different devices) break on
// the lexically-greater device id so all devices agree.
function isNewer(op: Operation): boolean {
  const vkey = `${op.collection}/${op.key ?? ''}`;
  const prev = versions[vkey];
  if (!prev) return true;
  if (op.lamport !== prev.lamport) return op.lamport > prev.lamport;
  return op.deviceId > prev.deviceId;
}

function recordVersion(op: Operation): void {
  const vkey = `${op.collection}/${op.key ?? ''}`;
  versions[vkey] = { lamport: op.lamport, deviceId: op.deviceId };
  persistVersions();
}

// ---- Collection registry --------------------------------------------------
// Maps each op `collection` to how it reads/writes IndexedDB, reusing db.ts as
// the actual storage layer. Two shapes:
//   • list  — an array of rows addressed by id (default) or a custom keyOf;
//             supports upsert/delete.
//   • value — a single blob replaced wholesale; supports set.
// `adjustStock` is handled out-of-band (see applyOp) because it composes deltas
// rather than replacing rows.

interface ListReg {
  kind: 'list';
  get: () => Promise<any[]>;
  save: (arr: any[]) => Promise<void>;
  keyOf: (row: any) => string;
}
interface ValueReg {
  kind: 'value';
  get: () => Promise<any>;
  save: (v: any) => Promise<void>;
}
type Reg = ListReg | ValueReg;

const byId = (row: any): string => row.id;

const list = (
  get: () => Promise<any[]>,
  save: (arr: any[]) => Promise<void>,
  keyOf: (row: any) => string = byId
): ListReg => ({ kind: 'list', get, save, keyOf });

const value = (get: () => Promise<any>, save: (v: any) => Promise<void>): ValueReg => ({
  kind: 'value',
  get,
  save,
});

const REGISTRY: Record<string, Reg> = {
  // list collections (upsert/delete by id)
  products: list(db.getProducts, db.saveProducts),
  sales: list(db.getSales, db.saveSales),
  customers: list(db.getCustomers, db.saveCustomers),
  suppliers: list(db.getSuppliers, db.saveSuppliers),
  salesmen: list(db.getSalesmen, db.saveSalesmen),
  servicePoints: list(db.getServicePoints, db.saveServicePoints),
  coupons: list(db.getCoupons, db.saveCoupons),
  recipes: list(db.getRecipes, db.saveRecipes),
  categories: list(db.getCategories, db.saveCategories),
  unitGroups: list(db.getUnitGroups, db.saveUnitGroups),
  attributes: list(db.getAttributes, db.saveAttributes),
  onlineOrders: list(db.getOnlineOrders, db.saveOnlineOrders),
  cashflow: list(db.getCashFlow, db.saveCashFlow),
  movements: list(db.getMovements, db.saveMovements),
  orders: list(db.getOrders, db.saveOrders),
  rooms: list(db.getRooms, db.saveRooms),
  roomBookings: list(db.getRoomBookings, db.saveRoomBookings),
  tableOrders: list(db.getTableOrders, db.saveTableOrders),
  tableLocks: list(db.getTableLocks, db.saveTableLocks, (l) => l.tableName),
  kots: list(db.getKots, db.saveKots),
  shifts: list(db.getShifts, db.saveShifts),
  // vehicles are keyed by (case-insensitive) vehicle number, not an id
  vehicles: list(db.getVehicles, db.saveVehicles, (v) => v.vehicleNo.trim().toUpperCase()),

  // singleton value collections (set the whole blob)
  config: value(db.getConfig, db.saveConfig),
  appSettings: value(db.getAppSettings, db.saveAppSettings),
  printSettings: value(db.getPrintSettings, db.savePrintSettings),
  activeShift: value(db.getActiveShift, db.setActiveShift),
  tables: value(db.getTables, db.saveTables),
  discountSlabs: value(db.getDiscountSlabs, db.saveDiscountSlabs),
};

// The collections the sync layer replicates, in a stable order (value blobs
// first so lists that reference settings load against them).
export const SYNCED_COLLECTIONS = Object.keys(REGISTRY);

// ---- Change notifications -------------------------------------------------
// applyOp is storage-only; the React layer (PosContext, useRestaurant) listens
// for this event to reload the affected collection into component state when an
// op is applied that it didn't originate (i.e. one arriving from the server).
export const COLLECTION_CHANGED = 'sconnect:collection-changed';

function notifyChanged(collection: string): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(COLLECTION_CHANGED, { detail: { collection } }));
  }
}

// ---- The single write path ------------------------------------------------
// Persist one operation into IndexedDB. Deterministic and idempotent enough to
// be replayed: applying the same upsert twice is a no-op, a delete of a missing
// row is a no-op. Used by both local mutations (emitOp) and remote ones
// (applyRemoteOp). Set `notify` to reload React state (remote ops); local
// callers pass false because they update their own state directly.
export async function applyOp(op: Operation, notify: boolean): Promise<void> {
  if (op.collection === 'stock' && op.action === 'adjustStock') {
    await db.adjustStock(op.value as { productId: string; variantId?: string; delta: number }[]);
    if (notify) notifyChanged('products');
    return;
  }

  const reg = REGISTRY[op.collection];
  if (!reg) {
    console.warn('applyOp: unknown collection', op.collection);
    return;
  }

  if (reg.kind === 'value') {
    await reg.save(op.value);
  } else {
    const arr = await reg.get();
    if (op.action === 'delete') {
      await reg.save(arr.filter((row) => reg.keyOf(row) !== op.key));
    } else {
      // upsert: replace the matching row in place, else append.
      const idx = arr.findIndex((row) => reg.keyOf(row) === op.key);
      if (idx >= 0) arr[idx] = op.value;
      else arr.push(op.value);
      await reg.save(arr);
    }
  }

  if (notify) notifyChanged(op.collection);
}

// ---- Serialized apply queue ----------------------------------------------
// applyOp does read-modify-write on a collection array, so two applies racing
// on the same collection could lose one change (both read the old array). We
// funnel every apply — local emits and remote ops alike — through one promise
// chain so they run strictly one-at-a-time. This is the ordering guarantee the
// old whole-array write-through got for free by living inside serialized React
// state updates.
let queue: Promise<unknown> = Promise.resolve();

function enqueue<T>(task: () => Promise<T>): Promise<T> {
  const run = queue.then(task, task);
  // Keep the chain alive regardless of individual task outcome.
  queue = run.then(
    () => undefined,
    () => undefined
  );
  return run as Promise<T>;
}

// ---- Emitting local ops ---------------------------------------------------
// A sync transport (Phase 1) registers here to receive every op this device
// produces, so it can send it to the server and hold it in the offline outbox.
// Until one registers (Phase 0, or "server off"), emitted ops are simply
// applied locally and go nowhere else.
type OpSink = (op: Operation) => void;
let sink: OpSink | null = null;

export function registerOpSink(fn: OpSink | null): void {
  sink = fn;
}

// Build, locally persist, and hand off one operation. Returns the finished op
// (with its Lamport stamp) in case the caller wants its id. The local
// applyOp runs with notify=false: the mutator that called emitOp updates its
// own React state, so re-notifying would be redundant work.
export async function emitOp(
  collection: string,
  action: OpAction,
  fields: { key?: string; value?: unknown }
): Promise<Operation> {
  const op: Operation = {
    id: newId(),
    deviceId: getDeviceId(),
    lamport: nextLamport(),
    ts: new Date().toISOString(),
    collection,
    action,
    key: fields.key,
    value: fields.value,
  };
  recordVersion(op);
  await enqueue(() => applyOp(op, false));
  if (sink) sink(op);
  return op;
}

// Convenience wrappers matching the common shapes.
export const opUpsert = (collection: string, key: string, value: unknown): Promise<Operation> =>
  emitOp(collection, 'upsert', { key, value });

export const opDelete = (collection: string, key: string): Promise<Operation> =>
  emitOp(collection, 'delete', { key });

export const opSet = (collection: string, value: unknown): Promise<Operation> =>
  emitOp(collection, 'set', { value });

export const opAdjustStock = (
  deltas: { productId: string; variantId?: string; delta: number }[]
): Promise<Operation> => emitOp('stock', 'adjustStock', { value: deltas });

// ---- Applying remote ops (Phase 1) ----------------------------------------
// Apply one op received from the server. Guards on the Lamport version map so a
// stale/duplicate op can't undo a newer local change, advances our clock past
// it, then reloads the affected React state. `stock`/adjustStock is commutative,
// so it always applies (no LWW guard).
export async function applyRemoteOp(op: Operation): Promise<void> {
  observeLamport(op.lamport);
  if (op.collection === 'stock') {
    await enqueue(() => applyOp(op, true));
    return;
  }
  if (!isNewer(op)) return;
  recordVersion(op);
  await enqueue(() => applyOp(op, true));
}

// ---- Full-snapshot helpers (Phase 1 join / adopt) -------------------------
// Gather every synced collection's current value — used to SEED an empty server
// from the first terminal's data.
export async function snapshotLocal(): Promise<Record<string, unknown>> {
  const out: Record<string, unknown> = {};
  for (const name of SYNCED_COLLECTIONS) {
    const reg = REGISTRY[name];
    out[name] = reg.kind === 'list' ? await reg.get() : await reg.get();
  }
  return out;
}

// Overwrite local collections with an authoritative snapshot pulled from the
// server (used when a terminal joins/reconnects). Only known collections are
// touched; unmanaged local data (users, roles, shifts, …) is left alone. Fires
// COLLECTION_CHANGED per collection so React state reloads.
export async function applySnapshot(collections: Record<string, unknown>): Promise<void> {
  for (const name of SYNCED_COLLECTIONS) {
    if (!(name in collections)) continue;
    const reg = REGISTRY[name];
    const value = collections[name];
    if (reg.kind === 'list') {
      await enqueue(() => reg.save((value as any[]) ?? []));
    } else {
      await enqueue(() => reg.save(value));
    }
    notifyChanged(name);
  }
}

// Awaited at boot (after initDb) so the version map is available before the
// first op is applied.
export async function initOps(): Promise<void> {
  await loadVersions();
}
